// =============================================================================
// features/pwa/platform.ts — Quirks de plataforma para o opt-in de push
// (doc 24 §11).
//
// `detectPushUnsupportedReason` recebe input explícito (sem tocar em
// `navigator`/`window` diretamente) para ser testável isolada; os helpers que
// leem o browser (`isStandaloneDisplayMode`) ficam separados.
// =============================================================================

export type PushUnsupportedReason = 'ios-not-installed' | 'unsupported-browser';

export interface PushSupportInput {
  /** Resultado de `isPushSupported()` — serviceWorker + PushManager + Notification. */
  supported: boolean;
  userAgent: string;
  /** App rodando em modo standalone (instalado na tela de início). */
  standalone: boolean;
}

/**
 * Decide a razão de indisponibilidade do push para orientar a mensagem certa:
 *   - iOS/iPadOS fora do modo standalone: Safari só expõe Web Push a apps
 *     instalados na tela de início (doc 24 §11) — orientar a instalar.
 *   - Qualquer outro navegador sem suporte a Push API/Notification.
 * Retorna `null` quando o push é suportado (nada a explicar).
 */
export function detectPushUnsupportedReason(input: PushSupportInput): PushUnsupportedReason | null {
  if (input.supported) return null;
  const isIOS = /iphone|ipad|ipod/i.test(input.userAgent);
  return isIOS && !input.standalone ? 'ios-not-installed' : 'unsupported-browser';
}

/** `true` quando o app roda instalado (standalone) — iOS e navegadores padrão. */
export function isStandaloneDisplayMode(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const mediaStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || mediaStandalone;
}

/** `true` quando o browser expõe as APIs necessárias ao Web Push. */
export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// ---------------------------------------------------------------------------
// Instalação do app na tela de início (F27-S10, doc 24 §11)
// ---------------------------------------------------------------------------

/**
 * Plataforma do ponto de vista da INSTALAÇÃO — cada valor tem um roteiro
 * diferente e mutuamente exclusivo:
 *   - `ios-safari`     → Compartilhar → Adicionar à Tela de Início (manual).
 *   - `ios-in-app`     → navegador embutido (WhatsApp, Instagram, Facebook):
 *                        o menu Compartilhar do Safari nem existe ali. É o caso
 *                        mais comum na prática — o operador recebe o link no
 *                        WhatsApp e toca nele. Só resolve abrindo no Safari.
 *   - `android-in-app` → mesma armadilha no Android.
 *   - `android`        → Chrome instala em um toque (`beforeinstallprompt`);
 *                        se o evento não vier, menu ⋮ → Instalar aplicativo.
 *   - `desktop`        → nada a instalar no celular aqui; copiar o link.
 */
export type InstallPlatform =
  | 'ios-safari'
  | 'ios-in-app'
  | 'android'
  | 'android-in-app'
  | 'desktop';

/**
 * Navegadores embutidos de apps (webview) onde não há como adicionar à tela de
 * início. Detectados pelo token que cada app injeta no user-agent:
 * `FBAN`/`FBAV` (Facebook), `Instagram`, `Line`, `MicroMessenger` (WeChat).
 * O WhatsApp no Android usa `wv` (webview do sistema); no iOS ele não injeta
 * token próprio — por isso o iOS também depende de `standaloneCapable`.
 */
const IN_APP_TOKENS = /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|; wv\)/i;

export interface InstallPlatformInput {
  userAgent: string;
  /**
   * `typeof navigator.standalone === 'boolean'` — no iOS a propriedade só
   * existe no Safari de verdade. Webviews (WhatsApp, Instagram, Chrome iOS
   * antigo) não a expõem, o que separa `ios-safari` de `ios-in-app` sem
   * depender de token de user-agent (o WhatsApp iOS não injeta nenhum).
   */
  standaloneCapable: boolean;
}

export function detectInstallPlatform(input: InstallPlatformInput): InstallPlatform {
  const ua = input.userAgent;
  // iPadOS 13+ se apresenta como Macintosh; `Mobile/` no UA continua presente.
  const isIOS = /iphone|ipad|ipod/i.test(ua) || (/Macintosh/i.test(ua) && /Mobile\//i.test(ua));
  const isAndroid = /android/i.test(ua);
  const inApp = IN_APP_TOKENS.test(ua);

  if (isIOS) return inApp || !input.standaloneCapable ? 'ios-in-app' : 'ios-safari';
  if (isAndroid) return inApp ? 'android-in-app' : 'android';
  return 'desktop';
}

/** Lê o browser real para alimentar `detectInstallPlatform`. */
export function currentInstallPlatform(): InstallPlatform {
  if (typeof navigator === 'undefined') return 'desktop';
  return detectInstallPlatform({
    userAgent: navigator.userAgent,
    standaloneCapable:
      typeof (navigator as Navigator & { standalone?: boolean }).standalone === 'boolean',
  });
}
